import { useEffect, useState, type JSX } from 'react'
import { Dialog } from '@base-ui/react/dialog'
import { AlertDialog } from '@base-ui/react/alert-dialog'
import { Container, WindowPortal, OverlayContainer } from './PopUp.styled'
import { PopUpForm, type PopUpSkeletonProps } from './PopUpForm.component'

export interface PopUpProps extends PopUpSkeletonProps {
  autoHideOnSubmit?: boolean
  disableAutoHideOnClose?: boolean
}

// Private composition shared with the destructive confirmation; the public facades keep their props.
export const PopUpWindow = (props: PopUpProps & { alert?: boolean }): JSX.Element => {
  const {
    alert = false,
    autoHideOnSubmit,
    disableAutoHideOnClose,
    children,
    onSubmit,
    onClose,
    onDismiss,
    onOpen,
    ...skeletonProps
  } = props

  useEffect(() => {
    onOpen?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [display, setDisplay] = useState<boolean>(false)

  useEffect(() => {
    setTimeout(() => {
      setDisplay(true)
    }, 10)
  }, [])

  const Root = alert ? AlertDialog.Root : Dialog.Root

  return (
    <Root
      open={display}
      {...(!alert && { disablePointerDismissal: true })}
      onOpenChange={(open, details) => {
        if (!open && details.reason === 'escape-key') (onDismiss ?? onClose)()
      }}
    >
      <WindowPortal
        container={document.getElementById('root')}
        keepMounted
        style={{ pointerEvents: display ? 'auto' : 'none' }}
      >
        <OverlayContainer forceRender />
        <Container id="popup">
          <PopUpForm
            dialog
            alert={alert}
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()

              if (autoHideOnSubmit) setDisplay(false)

              // Settles with the caller's own result so the skeleton keeps the submit locked until its work is done.
              return new Promise((resolve) => {
                setTimeout(() => {
                  try {
                    resolve(onSubmit(e))
                  } catch (error) {
                    // A synchronous throw must not leave the submit locked; the error still surfaces as before.
                    resolve(undefined)
                    throw error
                  }
                }, 150)
              })
            }}
            onClose={() => {
              if (!disableAutoHideOnClose) setDisplay(false)

              setTimeout(() => {
                onClose()
              }, 150)
            }}
            onDismiss={
              onDismiss
                ? () => {
                    // dismiss always hides immediately: disableAutoHideOnClose only makes sense for action flows
                    setDisplay(false)

                    setTimeout(() => {
                      onDismiss()
                    }, 150)
                  }
                : undefined
            }
            {...skeletonProps}
          >
            {children}
          </PopUpForm>
        </Container>
      </WindowPortal>
    </Root>
  )
}
